import AdjustIcon from "@mui/icons-material/Adjust";
import DeleteIcon from "@mui/icons-material/Delete";
import Box from "@mui/joy/Box";
import CircularProgress from "@mui/joy/CircularProgress";
import Divider from "@mui/joy/Divider";
import IconButton from "@mui/joy/IconButton";
import Link from "@mui/joy/Link";
import ModalClose from "@mui/joy/ModalClose";
import Stack from "@mui/joy/Stack";
import Typography from "@mui/joy/Typography";
import React from "react";
import { EventSource } from "../../../../models/clinical_event";
import { unixToDayjs } from "../../../utils/time";
import { NoteQuartzClientContext } from "../../../services/api/contexts";

interface ProofChipDrawerContentProps {
  readonly eventSources: EventSource[];
  readonly name: string;
}

export default function ProofChipDrawerContent({
  eventSources,
  name,
}: ProofChipDrawerContentProps) {
  const noteQuartzClient = React.useContext(NoteQuartzClientContext);

  // State to track which annotation is being confirmed or deleted
  const [confirmingDelete, setConfirmingDelete] = React.useState<string | null>(null);
  const [deleting, setDeleting] = React.useState<string | null>(null);

  // First click: show confirmation UI
  const handleDeleteClick = (id: string) => {
    setConfirmingDelete((prev) => (prev === id ? null : id));
  };

  // On confirm: perform async deletion
  const handleConfirmDelete = async (eventSource: EventSource, id: string) => {
    setDeleting(id);
    try {
      await noteQuartzClient.deleteFact(eventSource.note_id, eventSource.fact_id);
    } catch (error) {
      console.error(error);
    } finally {
      setDeleting(null);
      setConfirmingDelete(null);
    }
  };

  return (
    <>
      <Box sx={{ p: 2, display: "flex", alignItems: "center" }}>
        <Typography level="title-md" sx={{ flex: 1 }}>
          {name}
        </Typography>
        <ModalClose />
      </Box>
      <Divider />
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          p: 2,
        }}
      >
        {eventSources.map((eventSource, idx) => (
          <div key={`${eventSource.note_id || eventSource.fact_id || idx}`}>
            {eventSource.annotation.map((annotation, key) => {
              const id = `${idx}-${key}`;
              return (
                <Box
                  key={`${annotation.lexical_variant}-${annotation.snippet_before}-${key}`}
                  sx={{ display: "flex", gap: 1, width: "100%", mt: 1 }}
                >
                  <AdjustIcon color={eventSource.validation_date ? "success" : "warning"} />
                  <Stack direction="column" spacing={0.5} sx={{ width: "100%" }}>
                    <Typography fontWeight="lg" fontSize="sm">
                      "{annotation.snippet_before} {annotation.lexical_variant}{" "}
                      {annotation.snippet_after}"
                    </Typography>
                    <Typography
                      textColor="text.secondary"
                      fontSize="sm"
                      sx={{ mb: 1 }}
                      startDecorator={
                        <Link
                          underline="always"
                          href={`/note/${eventSource.note_id}`}
                          color="neutral"
                          fontSize="sm"
                          target="_blank"
                        >
                          {eventSource.note_title}
                        </Link>
                      }
                    >
                      on {unixToDayjs(eventSource.note_date)?.format("DD MMM YYYY")} -
                      <IconButton
                        size="sm"
                        variant="plain"
                        color="neutral"
                        onClick={() => handleDeleteClick(id)}
                      >
                        <DeleteIcon fontSize="small" color="action" />
                      </IconButton>
                    </Typography>
                    {/* Confirmation text */}
                    {confirmingDelete === id && !deleting && (
                      <Typography fontSize="xs" textColor="warning" sx={{ mt: 0.5 }}>
                        Are you sure?{" "}
                        <Link
                          component="button"
                          fontSize="xs"
                          onClick={() => handleConfirmDelete(eventSource, id)}
                        >
                          Confirm
                        </Link>{" "}
                        <Link
                          component="button"
                          fontSize="xs"
                          onClick={() => setConfirmingDelete(null)}
                        >
                          Cancel
                        </Link>
                      </Typography>
                    )}
                    {/* Loading indicator */}
                    {deleting === id && <CircularProgress size="sm" sx={{ mt: 0.5 }} />}
                  </Stack>
                </Box>
              );
            })}
          </div>
        ))}
      </Box>
    </>
  );
}
